import { useState, useEffect } from 'react'
import { getCurrentChildId, setCurrentChildId, getChildToken, getStoredToken, clearChildToken, getTokenPayload } from '../auth.js'
import { api } from '../api.js'
import ChildHome from './ChildHome.jsx'

export default function ChildApp() {
   const [children, setChildren] = useState([])
   const [currentChildId, setCurrentChildIdState] = useState(() => getCurrentChildId())
   const [loading, setLoading] = useState(true)
   const [questTheme, setQuestTheme] = useState(null)
   const [lockedChildId, setLockedChildId] = useState(null)

   useEffect(() => {
     // No child token — redirect appropriately
     if (!getChildToken()) {
       // Parent token present → send to parent dashboard
       if (getStoredToken()) {
         window.location.replace('/dashboard')
       } else {
         window.location.replace('/login')
       }
       return
     }

     // Check if token is child-locked
     const payload = getTokenPayload()
     const childIdFromToken = payload?.childId || null
     if (childIdFromToken) {
       setLockedChildId(childIdFromToken)
       setCurrentChildId(childIdFromToken)
       setCurrentChildIdState(childIdFromToken)
     }

     api.childDevice.getFamily()
       .then(data => {
         setChildren(data.children || [])
         setQuestTheme(data.quest_theme || null)
       })
       .catch((err) => {
         // Only clear token on auth errors (401), not on DB/server errors
         if (err?.status === 401 || err?.message?.includes('401')) {
           clearChildToken()
           window.location.href = '/login'
         }
         // Otherwise stay on page — server error shouldn't log child out
       })
       .finally(() => setLoading(false))
   }, [])

  function selectChild(id) {
    setCurrentChildId(id)
    setCurrentChildIdState(id)
  }

  if (loading) return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-violet-500 border-t-transparent" />
    </div>
  )

  const currentChild = children.find(c => c.id === currentChildId)

  if (!currentChild) {
    return <ChildPicker children={children} onSelect={selectChild} theme={questTheme} />
  }

   return <ChildHome child={currentChild} children={children} theme={questTheme} onSwitchChild={() => setCurrentChildIdState(null)} locked={!!lockedChildId} />
}

function ChildPicker({ children, onSelect, theme }) {
  const EMOJIS = ['🦁', '🐯', '🐻', '🦊', '🐼', '🐨', '🦄', '🐸']

  const THEME_BG = {
    space: 'from-indigo-900 to-slate-900',
    fantasy: 'from-purple-900 to-slate-900',
    pirates: 'from-amber-900 to-slate-900',
    nature: 'from-emerald-900 to-slate-900',
    default: 'from-violet-900 to-slate-900',
  }

  const bgClass = THEME_BG[theme] || THEME_BG.default

  return (
    <div className={`flex min-h-screen flex-col items-center justify-center gap-6 bg-gradient-to-b ${bgClass} p-6`}>
      <p className="text-4xl">👋</p>
      <h1 className="text-2xl font-bold text-white">Vem spelar?</h1>
      <div className="grid grid-cols-2 gap-4 w-full max-w-xs">
        {children.map((child, i) => (
          <button
            key={child.id}
            onClick={() => onSelect(child.id)}
            className="flex flex-col items-center gap-2 rounded-2xl bg-white/10 p-5 text-white transition hover:bg-white/20 active:scale-95"
          >
            <span className="text-5xl">{child.avatar_emoji || EMOJIS[i % EMOJIS.length]}</span>
            <span className="text-base font-semibold">{child.name}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
